import isEqual from 'lodash/isEqual';
import React, { Component } from 'react';
import isIE from '@framework/browser/isIE';

interface IProps {
  component: React.ElementType,
  parent?: string | number,
  name: string | number,
  dataTest: string | number,
  label: string | number,
  defaultChecked?: boolean | number,
  disabled?: boolean,
  supportIndeterminate?: boolean,
  supportCheckDisabled?: boolean,
  indeterminate?: boolean | number,
  onChange: (payload: any) => void,
  registerField: (payload: any) => void,
  unregisterField: (name: string | number) => void,
  fieldChange: (payload: any) => void,
  getCheckboxChildren: (checkboxes: any, name: string | number) => any,
  checkboxes?: any,
  controlClassName: string,
  labelClassName: string,
}

const defaultProps: Partial<IProps> = {
  // @ts-ignore
  parent: null,
  checkboxes: {},
  defaultChecked: false,
  disabled: false,
  supportIndeterminate: false,
  supportCheckDisabled: false,
  indeterminate: false,
};

/**
 * @deprecated This component is considered very legacy and should not be used.
 * It is recommended to avoid any further use of this component due to its outdated nature.
 * Consider using modern alternative instead such as CheckBoxArray. See Example in shared/components/Tables/Columns
 */
class CheckboxField extends Component<IProps, any> {
  static defaultProps: Partial<IProps> = defaultProps;

  /**
   * @param {Object} props
   */
  constructor(props) {
    super(props);
    this.handleChange = this.handleChange.bind(this);
    this.getIndeterminateState = this.getIndeterminateState.bind(this);
    this.handleIEIndeterminateClick = this.handleIEIndeterminateClick.bind(this);
    this.getState = this.getState.bind(this);
  }

  componentDidMount() {
    const {
      parent,
      name,
      defaultChecked,
      disabled,
      indeterminate,
      supportIndeterminate,
      supportCheckDisabled,
    } = this.props;
    this.props.registerField({
      parent,
      name,
      checked: defaultChecked,
      disabled,
      indeterminate,
      supportIndeterminate,
      supportCheckDisabled,
    });

    if (supportIndeterminate) {
      if (name in this.props.checkboxes) {
        let indeterminateState = this.getIndeterminateState(this.props.checkboxes, name);
        this.props.fieldChange({
          parent,
          name,
          disabled,
          supportIndeterminate,
          supportCheckDisabled,
          ...indeterminateState,
        });
      }
    }
  }

  /**
   * @param {Object} prevProps
   */
  componentDidUpdate(prevProps) {
    const { parent, name, disabled, defaultChecked, supportCheckDisabled, supportIndeterminate } = this.props;
    if (supportIndeterminate) {
      const indeterminateState = this.getIndeterminateState(this.props.checkboxes, name);
      const state = this.getState(this.props.checkboxes, name);
      if (
        !isEqual(
          this.props.getCheckboxChildren(prevProps.checkboxes, prevProps.name),
          this.props.getCheckboxChildren(this.props.checkboxes, name),
        )
      ) {
        this.props.fieldChange({
          parent,
          name,
          disabled,
          ...state,
          ...indeterminateState,
        });
      }
    }

    if (prevProps.supportCheckDisabled !== supportCheckDisabled ||
        prevProps.disabled !== disabled ||
        prevProps.defaultChecked !== defaultChecked) {
      const state = this.getState(this.props.checkboxes, name);
      this.props.fieldChange({
        name,
        ...state,
        ...(prevProps.supportCheckDisabled !== supportCheckDisabled) && { supportCheckDisabled },
        ...(prevProps.disabled !== disabled) && { disabled },
        ...(prevProps.defaultChecked !== defaultChecked) && { checked: defaultChecked },
      });
    }
  }

  componentWillUnmount() {
    this.props.unregisterField(this.props.name);
  }

  getIndeterminateState(checkboxes, name) {
    if (Object.keys(checkboxes).length) {
      const children = this.props.getCheckboxChildren(checkboxes, name);
      if (Object.keys(children).length) {
        let activeChildren = Object
          .keys(children)
          .filter((key) => !children[key].disabled);

        let checkedChildren = Object
          .keys(children)
          .filter((key) => children[key].checked && !children[key].disabled);

        const isAllSupportCheckDisabled = Object
          .values(children)
          .every((checkbox: any) => checkbox.supportCheckDisabled);

        if (isAllSupportCheckDisabled) {
          const checkedSupportCheckDisabledChildren = Object.keys(children).filter((key) => (
              children[key].checked && children[key].disabled && children[key].supportCheckDisabled
            ));
          const supportCheckDisabledChildren = Object.keys(children).filter((key) => (
              children[key].disabled && children[key].supportCheckDisabled
            ));

          activeChildren = [...activeChildren, ...supportCheckDisabledChildren];
          checkedChildren = [...checkedChildren, ...checkedSupportCheckDisabledChildren];
        }

        const isParentDisabled = this.getState(checkboxes, name).disabled;
        const isAllDisabled = Object.values(children).every((checkbox: any) => checkbox.disabled);
        if (isParentDisabled && isAllDisabled) {
          let checkedDisabledChildren = Object.keys(children).filter((key) => children[key].checked);
          activeChildren = [...Object.keys(children)];
          checkedChildren = [...checkedChildren, ...checkedDisabledChildren];
        }

        return {
          checked: (checkedChildren.length === 0) ? false :
            (activeChildren.length === checkedChildren.length),
          indeterminate: (checkedChildren.length !== 0) &&
                         (activeChildren.length !== checkedChildren.length),
        };
      }
      return this.getState(checkboxes, name);
    }
    return this.getState(checkboxes, name);
  }

  getState(checkboxes, name) {
    if (Object.keys(checkboxes).length && (name in checkboxes)) {
      return checkboxes[name];
    }
    return {};
  }

  handleChange(e) {
    const { parent, name, disabled, supportIndeterminate, supportCheckDisabled } = this.props;
    let fieldChildren = [];
    if (disabled) { return; }

    if (supportIndeterminate) {
      const toggle = this.getState(this.props.checkboxes, name).checked
        ? e.target.checked
        : !this.getState(this.props.checkboxes, name).checked;
      const children = this.props.getCheckboxChildren(this.props.checkboxes, name);
      const activeChildren = Object.keys(children).filter((key) => !children[key].disabled);
      const supportCheckDisabledChildren = Object.keys(children)
        .filter((key) => children[key].disabled && children[key].supportCheckDisabled);

      const items = supportCheckDisabled
        ? Object.keys(children)
        : [...activeChildren, ...supportCheckDisabledChildren];
      if (Object.keys(items).length) {
        items.forEach((key) => {
          const field = {
            parent: children[key].parent,
            name: key,
            checked: toggle,
            disabled: children[key].disabled,
            indeterminate: children[key].indeterminate,
            supportCheckDisabled: children[key].supportCheckDisabled,
          };
          // @ts-ignore
          fieldChildren.push(field);
          this.props.fieldChange(field);
        });
      }
    }

    const field = {
      parent,
      name: e.target.name,
      checked: e.target.checked,
      disabled: e.target.disabled,
      indeterminate: supportIndeterminate
        ? this.getIndeterminateState(this.props.checkboxes, name).indeterminate
        : false,
    };
    this.props.fieldChange(field);

    if (this.props.onChange) {
      this.props.onChange({
        ...field,
        children: supportIndeterminate ? fieldChildren : [],
      });
    }
  }

  handleIEIndeterminateClick() {
    let stateProps = this.getState(this.props.checkboxes, this.props.name);
    // I'm thankful for a happy childhood with IE
    if (isIE()) {
      if (stateProps.indeterminate) {
        this.handleChange({
          target: {
            checked: true,
            disabled: stateProps.disabled,
            indeterminate: stateProps.indeterminate,
          },
        });
      }
    }
  }

  /**
   * @return {ReactElement}
   */
  render() {
    const { component: Component, onChange, indeterminate, ...otherProps } = this.props;
    let stateProps = this.getState(this.props.checkboxes, this.props.name);
    return (
      <Component
        onChange={ this.handleChange }
        onClick={ this.handleIEIndeterminateClick }
        { ...otherProps }
        { ...stateProps }
      />
    );
  }
}

export default CheckboxField;
